import { useTranslation } from 'react-i18next';
import { Switch } from 'components';
import { formatLanguageCode } from 'utils/audioUpload';
import {
  AlwaysAvailableBadge,
  SettingDescription,
  SettingLabel,
  SettingRow,
  SettingsCard,
  SettingsCardContent,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsHint,
  SettingText,
} from './settings.styles';

const ALWAYS_AVAILABLE_LANGUAGES = [
  { code: 'de', descriptionKey: 'settingsScreen.defaultLanguage' },
  { code: 'en', descriptionKey: 'settingsScreen.highestQuality' },
];

const ADDITIONAL_LANGUAGES = [
  { code: 'fr', descriptionKey: 'settingsScreen.highestQuality' },
  { code: 'es', descriptionKey: 'settingsScreen.highQuality' },
  { code: 'ar', descriptionKey: 'settingsScreen.highQuality' },
];

interface LanguageSettingsCardProps {
  enabledLanguages: string[];
  disabled: boolean;
  onChange: (enabledLanguages: string[]) => void;
}

export const LanguageSettingsCard = ({
  enabledLanguages,
  disabled,
  onChange,
}: LanguageSettingsCardProps) => {
  const { t } = useTranslation();

  const toggle = (code: string, enabled: boolean) =>
    onChange(
      enabled
        ? [...enabledLanguages, code]
        : enabledLanguages.filter((enabledCode) => enabledCode !== code)
    );

  return (
    <SettingsCard>
      <SettingsCardHeader>
        <SettingsCardTitle>{t('settingsScreen.languagesSection')}</SettingsCardTitle>
      </SettingsCardHeader>
      <SettingsCardContent>
        {ALWAYS_AVAILABLE_LANGUAGES.map(({ code, descriptionKey }) => (
          <SettingRow key={code}>
            <SettingText>
              <SettingLabel as="span" $active>
                {t(`languages.${code}`)} ({formatLanguageCode(code)})
              </SettingLabel>
              <SettingDescription>{t(descriptionKey)}</SettingDescription>
            </SettingText>
            <AlwaysAvailableBadge>{t('settingsScreen.alwaysAvailable')}</AlwaysAvailableBadge>
          </SettingRow>
        ))}
        {ADDITIONAL_LANGUAGES.map(({ code, descriptionKey }) => {
          const enabled = enabledLanguages.includes(code);
          return (
            <SettingRow key={code}>
              <SettingText>
                <SettingLabel htmlFor={`language-${code}`} $active={enabled}>
                  {t(`languages.${code}`)}
                </SettingLabel>
                <SettingDescription id={`language-${code}-description`}>
                  {t(descriptionKey)}
                </SettingDescription>
              </SettingText>
              <Switch
                id={`language-${code}`}
                aria-describedby={`language-${code}-description`}
                checked={enabled}
                disabled={disabled}
                onCheckedChange={(checked: boolean) => toggle(code, checked)}
              />
            </SettingRow>
          );
        })}
        <SettingsHint>{t('settingsScreen.languagesHint')}</SettingsHint>
      </SettingsCardContent>
    </SettingsCard>
  );
};
